import classNames from "classnames";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advanced from "dayjs/plugin/advancedFormat";
import { BasicTable } from "../../../atoms/BasicTable";
import { Label } from "../../../atoms/Label";
import { Typography } from "components/atoms";
import { InstructorRecord } from "./InstructorRecord";
import { decodeToken } from "utils/functions/decodeToken";
import { useGetOverallProgressQuery } from "services/homeApi";
import { Spinner } from "components/atoms/Icon";
import { formatToLocalDate } from "utils/functions/formatToLocalDate";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/molecules/ToolTip";
import { getTooltipContentByStatus } from "utils/functions/tooltipContentByStatus";
import "./topic-focus-table.scss";

interface IProps {
  data?: any;
  type?: string;
  theme?: "light" | "dark";
  isTableBodyScrolled?: boolean;
}

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);
const guessedTimezone = dayjs.tz.guess();

export const TopicFocusTable = ({
  data,
  type,
  theme,
  isTableBodyScrolled,
}: IProps) => {
  const payload = decodeToken();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const { isFetching } = useGetOverallProgressQuery({
    Userid: userId,
    Productcode: productCode,
    Offeringcode: offeringCode,
  });

  const getMatchColor = (status: string) => {
    switch (status) {
      case "Past Due":
      case "Time Limit Exceeded":
      case "Not Attended":
        return "red";
      case "Completed":
      case "Complete":
      case "Attended":
      case "Viewed Recording":
      case "Watched Recording":
        return "green";
      default:
        return "gray";
    }
  };

  return (
    <BasicTable
      rules="none"
      columns={[
        { title: "Class Name", key: "Name" },
        { title: "Class Date", key: "classDate" },
        { title: "Instructor Name", key: "instructorName" },
        { title: "Date Completed", key: "completedOn" },
        { title: "Status", key: "status" },
      ]}
      data={data}
      renderHead={(customColumns) => (
        <thead
          className={classNames({
            "tests-details-thead": true,
            "has-scrolled": isTableBodyScrolled,
          })}
        >
          <tr>
            {customColumns.map((obj, index) => (
              <th key={index + obj.title}>{obj.title}</th>
            ))}
          </tr>
        </thead>
      )}
      renderBody={(customData) => (
        <tbody className={classNames("topic-focus-tbody")}>
          {isFetching ? (
            <tr>
              <td colSpan={5}>
                <div className="topic-focus-tbody-spin-wrapper">
                  <Spinner theme={theme} />
                </div>
              </td>
            </tr>
          ) : customData && customData.length > 0 ? (
            customData.map((obj, index) => (
              <tr
                key={index}
                className={classNames({
                  highlight: index % 2 === 0,
                })}
              >
                {obj.Status === "Not Attended" || obj.Status === "Absent" ? (
                  <InstructorRecord
                    obj={obj}
                    getMatchColor={getMatchColor}
                    theme={theme}
                  />
                ) : (
                  <>
                    <td
                      className={classNames({
                        "test-name": true,
                      })}
                    >
                      {obj.Name}
                    </td>

                    <td>
                      {dayjs(obj.ClassDate).isValid()
                        ? `${formatToLocalDate(obj.ClassDate, "MM/DD/YY hh:mm A")} ${dayjs()
                            .tz(guessedTimezone)
                            .format("z")}`
                        : obj.ClassDate}
                    </td>
                    <td>
                      {obj.Instructors &&
                      Array.isArray(obj.Instructors) &&
                      obj.Instructors.length > 0
                        ? obj.Instructors[0].InstructorName
                        : "Unknown"}
                    </td>
                    <td>
                      {dayjs(obj.TimeEntered).isValid()
                        ? `${formatToLocalDate(obj.TimeEntered, "MM/DD/YY hh:mm A")} ${dayjs()
                            .tz(guessedTimezone)
                            .format("z")}`
                        : obj.TimeEntered}
                    </td>
                    <td
                      className={classNames({
                        status: true,
                      })}
                    >
                      <Tooltip>
                        <TooltipTrigger>
                          <Label
                            backgroundColor={getMatchColor(obj.Status)}
                            label={obj.Status}
                          />
                        </TooltipTrigger>
                        <TooltipContent
                          className={`status-topic-focus-tooltip-${theme}`}
                        >
                          <Typography
                            variant="h8Book"
                            label={getTooltipContentByStatus(obj.Status)}
                          />
                        </TooltipContent>
                      </Tooltip>
                    </td>
                  </>
                )}
              </tr>
            ))
          ) : (
            <div className="empty-data-wrapper">
              <Typography variant="h8Book" label="No data available" />
            </div>
          )}
        </tbody>
      )}
    />
  );
};
